import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, MapPin, Users, ExternalLink, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAppStore } from '../../store';
import { useCalendarNoteByKey } from '../../hooks/useCalendarNotes';
import { MeetingEditor } from './MeetingEditor';
import type { CalendarEvent } from '../../types/calendar';
import { format } from 'date-fns';

interface MeetingCanvasProps {
  event: CalendarEvent;
}

function extractZoomUrl(location?: string, notes?: string): string | null {
  const text = `${location ?? ''} ${notes ?? ''}`;
  const match = text.match(/(https?:\/\/[^\s]*zoom\.us[^\s]*)/i);
  return match ? match[1] : null;
}

function extractDisplayLocation(location?: string): string | null {
  if (!location) return null;
  const cleaned = location.replace(/https?:\/\/\S+/g, '').trim();
  if (!cleaned || cleaned === ',') return null;
  return cleaned;
}

export function MeetingCanvas({ event }: MeetingCanvasProps) {
  const setSelectedMeetingId = useAppStore((s) => s.setSelectedMeetingId);
  const { data: existingNote, isLoading } = useCalendarNoteByKey(event.id);
  const [showAllAttendees, setShowAllAttendees] = useState(false);

  const zoomUrl = extractZoomUrl(event.location, event.notes);
  const physicalLocation = extractDisplayLocation(event.location);
  const dateObj = new Date(event.date + 'T00:00:00');
  const formattedDate = format(dateObj, 'EEEE, MMMM d, yyyy');
  const visibleAttendees = showAllAttendees ? event.attendees : event.attendees.slice(0, 3);
  const hiddenCount = event.attendees.length - 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.15 }}
      className="h-full flex flex-col"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border-subtle">
        <button
          onClick={() => setSelectedMeetingId(null)}
          className="flex items-center gap-1.5 text-caption text-text-muted hover:text-text-primary transition-colors"
        >
          <ArrowLeft size={14} />
          Back
        </button>
        <h2 className="text-title text-text-primary truncate max-w-[60%] text-center">
          {event.title}
        </h2>
        <div className="w-16" />
      </div>

      {/* Cancelled banner */}
      {event.isCancelled && (
        <div className="mx-6 mt-4 px-4 py-2.5 rounded-lg bg-p2/10 border border-p2/20 flex items-center gap-2">
          <AlertTriangle size={14} className="text-p2 shrink-0" />
          <span className="text-caption text-p2">This meeting was cancelled</span>
        </div>
      )}

      {/* Meeting metadata */}
      <div className="mx-6 mt-4">
        <div className="surface-card p-4 space-y-3">
          {/* Time + Date */}
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-text-muted shrink-0" />
            <span className="text-title text-text-primary">
              {event.startTime} – {event.endTime}
            </span>
            <span className="text-body text-text-muted">·</span>
            <span className="text-body text-text-muted">{formattedDate}</span>
          </div>

          {/* Location / Zoom */}
          {(zoomUrl || physicalLocation) && (
            <div className="flex items-start gap-2">
              <MapPin size={14} className="text-text-muted shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                {zoomUrl && (
                  <a
                    href={zoomUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-caption text-accent hover:text-accent-hover transition-colors"
                  >
                    Join Zoom Meeting
                    <ExternalLink size={11} />
                  </a>
                )}
                {physicalLocation && (
                  <span className="text-caption text-text-secondary">{physicalLocation}</span>
                )}
              </div>
            </div>
          )}

          {/* Attendees */}
          {event.attendees.length > 0 && (
            <div className="flex items-start gap-2">
              <Users size={14} className="text-text-muted shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex flex-wrap gap-x-1.5 gap-y-1">
                  {visibleAttendees.map((name, i) => (
                    <span key={i} className="text-caption text-text-secondary">
                      {name}{i < visibleAttendees.length - 1 ? ' ·' : ''}
                    </span>
                  ))}
                </div>
                {hiddenCount > 0 && (
                  <button
                    onClick={() => setShowAllAttendees(!showAllAttendees)}
                    className="flex items-center gap-1 mt-1 text-micro text-text-muted hover:text-text-secondary transition-colors"
                  >
                    {showAllAttendees ? (
                      <>Show less <ChevronUp size={11} /></>
                    ) : (
                      <>+ {hiddenCount} more <ChevronDown size={11} /></>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Editor */}
      {!isLoading && (
        <div className="flex-1 min-h-0 mt-4 flex flex-col">
          <MeetingEditor
            meetingKey={event.id}
            meetingTitle={event.title}
            meetingDate={event.date}
            existingNote={existingNote ?? null}
          />
        </div>
      )}
    </motion.div>
  );
}
